import {createAsyncThunk} from '@reduxjs/toolkit';
import {getJWTToken} from '../../lib/auth-token';
import {handleAuthFailure, signOutExpiredSession} from '../../lib/auth-error-handler';
import {onSessionCleared} from '../../lib/sessionCleanup';
import {getWorkerApiUrl, apiFetch} from '../../lib/api';
import {debugLog} from '../../lib/debug';
import {ApiError, toApiError, toNetworkError} from '../../lib/apiError';
import {Transcription} from '../../domain/types';
import {usageReceived} from './usageSlice';
import {toUsageSnapshot} from './usageThunks';
import type {
  StartTranscriptionRequest,
  StartTranscriptionResponse,
  TranscriptionRecord,
  TranscriptionStatusResponse,
} from '../../backend-types/transcribe-api';
import type {TranscriptionUsage} from '../../backend-types/usage-api';

/**
 * The wire shape, which is `TranscriptionRecord` plus the two fields only the older CSV-import rows
 * carry. `duration` is the legacy spelling of `duration_seconds`.
 */
type WorkerTranscription = TranscriptionRecord & {
    sample_rate?: number;
    duration?: number;
    // The uploaded file's name. Not in the read-only wire copy yet, and optional here so a
    // backend that does not send it falls through to the audio record rather than failing to parse.
    audio_filename?: string | null;
};

/**
 * Wire → domain. Rows are no longer all finished: a job that is still running has `size: 0`,
 * nothing behind `r2_key`, and cannot be downloaded or converted until `status` says otherwise.
 */
function toTranscription(wire: WorkerTranscription): Transcription {
  return {
    id: wire.id,
    name: wire.name.replace('.csv', ''),
    csvR2Key: wire.r2_key,
    projectId: wire.project_id || 'all',
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
    audioPath: wire.audio_path || '', // Source media label (backend: audio_path)
    audioFilename: wire.audio_filename ?? null,
    // Rows predating server-side transcription have no status column; they are all finished.
    status: wire.status || 'completed',
    error: wire.error,
    audioUploadId: wire.audio_upload_id,
    sizeBytes: wire.size,
    sampleRate: wire.sample_rate,
    duration: wire.duration_seconds ?? wire.duration ?? undefined,
  };
}

// Store ETags for each project filter (including 'all' for no filter).
// Keyed by project id and not by user, so it has to be dropped when the session ends — otherwise the
// next account to sign in on this browser replays the previous one's If-None-Match.
const transcriptionsEtags = new Map<string, string>();

// Audio ids currently locked out of another `startTranscription` send. `TranscribeModal`'s own
// `isStarting` disables the button while its dialog is open, but that is one React instance's state
// — it does nothing for a double-click that lands before the re-render disables it, and nothing at
// all for `pendingTranscriptions.container` releasing a queued job for the same audio id at the
// moment the user separately clicks Retry on it. This lives beside the thunk instead, so every
// dispatcher (the workspace launch hook, the legacy transcriptions page, the queue release) shares
// one lock keyed on the thing that actually must not be double-sent — the audio id — regardless of
// which screen or timer fired the second request.
//
// **The lock deliberately ignores how long the request itself takes.** The first cut released it in
// a `finally` once the promise settled, but `/transcribe` answers in well under a second — that
// closed the window almost as soon as it opened, which is exactly fast enough for a human
// double-click to land inside it. A flat cooldown started the moment the first send is accepted does
// not care whether the response was fast or slow, so it is the thing actually worth relying on. The
// backend is being asked to enforce this too; this is still only the client-side half.
const startingAudioIds = new Set<string>();

/** How long a second send for the same audio id is refused, counted from the first send. */
export const DUPLICATE_SUBMIT_COOLDOWN_MS = 5000;

onSessionCleared(() => {
  transcriptionsEtags.clear();
  startingAudioIds.clear();
});

export const fetchTranscriptions = createAsyncThunk(
  'transcriptions/fetch',
  async (projectFilter: string, {rejectWithValue, getState, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      debugLog('fetchTranscriptions - JWT token:', jwt ? `${jwt.substring(0, 20)}...` : 'null');

      if (!jwt) {
        throw new Error('No authentication token available');
      }

            // Build API URL with project filter if provided
            // 'all' means fetch all transcriptions (no project filter)
      const apiUrl = getWorkerApiUrl();
      const cacheKey = projectFilter;
      const url = (projectFilter && projectFilter !== 'all')
        ? `${apiUrl}/rest/v1/transcriptions?projectId=${projectFilter}`
        : `${apiUrl}/rest/v1/transcriptions`;

            // Get stored ETag for this project filter
      const storedEtag = transcriptionsEtags.get(cacheKey);

      debugLog('Making GET request to:', url);
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${jwt}`,
      };

            // Add If-None-Match header if we have a stored ETag
      if (storedEtag) {
        debugLog('Sending If-None-Match:', storedEtag);
        headers['If-None-Match'] = storedEtag;
      } else {
        debugLog('No stored ETag - making fresh request');
      }

      const response = await apiFetch(url, {headers});

      debugLog('Response status:', response.status);

            // Extract ETag from response if present
      const newEtag = response.headers.get('etag');
      if (newEtag) {
                // Strip W/ prefix if present to store strong ETag
        const etag = newEtag.startsWith('W/') ? newEtag.slice(2) : newEtag;
        debugLog('ETag:', newEtag);
        transcriptionsEtags.set(cacheKey, etag);
      }

            // Handle 304 Not Modified - return cached data
      if (response.status === 304) {
        debugLog('304 Not Modified - data unchanged');
        const state = getState() as any;
        return state.transcriptions.transcriptions;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`Worker API returned error ${response.status}: ${errorText}`);
      }

            // Parse response
            interface TranscriptionsResponse {
                transcriptions: WorkerTranscription[];
            }

            const data: TranscriptionsResponse = await response.json();
            debugLog('Successfully parsed', data.transcriptions.length, 'transcriptions');

            return data.transcriptions.map(toTranscription);
    } catch (error) {
      const errorStr = String(error);

            // Check for 401/auth errors and auto-logout
      handleAuthFailure(error, dispatch);

      return rejectWithValue(errorStr);
    }
  }
);

export const deleteTranscription = createAsyncThunk(
  'transcriptions/delete',
  async ({key}: { key: string }, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
            // The key is the full path like: uploads/userId/project/timestamp_filename.csv
      const url = `${apiUrl}/rest/v1/transcriptions/${key}`;

      debugLog('Making DELETE request to:', url);
      const response = await apiFetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      debugLog('Delete response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete failed with status', response.status, ':', errorText);
        throw new Error(`Failed to delete transcription: ${response.status} - ${errorText}`);
      }

            // Clear all cached ETags since data has changed
      debugLog('Clearing all cached ETags after delete');
      transcriptionsEtags.clear();

      debugLog('Transcription deleted successfully');
      return key;
    } catch (error) {
            // Check for 401/auth errors and auto-logout
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * Both refusals `/transcribe` can answer with — the 402 paywall and the 429 session limit — carry
 * the balance the server just decided them with. Taking it means the header stops disagreeing with
 * the wall the user is looking at, without a second round trip, and it is the same numbers the
 * refusal was computed from rather than a re-read that may already have moved.
 */
function adoptUsageFromRejection(bodyText: string, dispatch: (action: unknown) => unknown): void {
  try {
    const body: {usage?: TranscriptionUsage} = JSON.parse(bodyText);

    if (body.usage) {
      dispatch(usageReceived(toUsageSnapshot(body.usage)));
    }
  } catch {
    // Not JSON, or no usage block — `/audio/upload-url` rejections never carry one. The caller
    // still gets its ApiError; the header simply corrects itself on the next read.
  }
}

/**
 * Step 2 of two: hand an already-uploaded file to AssemblyAI.
 *
 * Separate from the upload on purpose — both steps are slow, and one progress bar meaning two
 * different things is what we are avoiding. Returns as soon as the job is accepted (HTTP 202); the
 * CSV does not exist yet and `status` is `processing` until `pollTranscriptionStatus` says
 * otherwise. Retrying a failed job is this same call with the same `audioId`: the upload is still
 * there, so nothing has to be sent again.
 *
 * **A rejection here is not always a paywall.** 402 means the period allowance or the per-file cap
 * (`reason` says which) and an upgrade fixes it. 429 `session_limit_exceeded` means the rate
 * throttle — a wait no plan shortens — and 429 `too_many_in_flight` means three jobs are already
 * running, a much shorter wait. 409 `duplicate_in_flight` means this exact `audioId` already has a
 * job running — not an error to show, but a signal to adopt `transcriptionId` and resume polling it
 * (see `useTranscriptionLaunch`). Callers must branch on `reason`, not on the status.
 *
 * **Duplicate sends for the same audio id are refused client-side before the request goes out**, via
 * the `condition` below, which checks `startingAudioIds` and bails with no network call and no
 * dispatched action at all if one was sent in the last `DUPLICATE_SUBMIT_COOLDOWN_MS` — every caller
 * already treats an undefined `result.payload` as a silent no-op, so a blocked duplicate does nothing
 * rather than surfacing a spurious error. The lock releases itself on a timer, not in `finally`: it
 * does not matter whether this particular send succeeded, failed, or is still pending when it does.
 *
 * **This client-side guard is not the whole story.** It only sees one tab's in-memory state, so it
 * cannot catch a second tab, a reload that lost the lock mid-flight, or the queue in
 * `pendingTranscriptions.container` racing a manual click for the same file. The 409 above is the
 * server's backstop for exactly that gap — it is expected to fire occasionally and callers must
 * treat it as a reconciliation signal, not a failure.
 */
export const startTranscription = createAsyncThunk<
  Transcription,
  StartTranscriptionRequest,
  {rejectValue: ApiError}
>(
  'transcriptions/start',
  async (params, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const url = `${getWorkerApiUrl()}/rest/v1/transcribe`;
      debugLog('[Transcriptions] Starting transcription for audio', params.audioId);

      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Transcriptions] Start failed with status', response.status, ':', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        adoptUsageFromRejection(errorText, dispatch);

        return rejectWithValue(toApiError(response.status, errorText, 'Could not start the transcription'));
      }

      const data: StartTranscriptionResponse = await response.json();
      debugLog('[Transcriptions] Job accepted:', data.transcription.id, data.transcription.status);

      // The list is now stale — a new row exists that the cached ETag does not know about.
      transcriptionsEtags.clear();

      return toTranscription(data.transcription as WorkerTranscription);
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(toNetworkError(error, 'Could not start the transcription'));
    }
    // No `finally` here on purpose — releasing the lock as soon as this settles is exactly the
    // promise-tied behaviour that did not hold up; see `startingAudioIds` above.
  },
  {
    condition: (params) => {
      if (startingAudioIds.has(params.audioId)) {
        debugLog('[Transcriptions] Ignoring duplicate start for audio', params.audioId, '— cooldown active');
        return false;
      }

      startingAudioIds.add(params.audioId);
      setTimeout(() => startingAudioIds.delete(params.audioId), DUPLICATE_SUBMIT_COOLDOWN_MS);
      return true;
    },
  }
);

/**
 * One poll of a running job. The caller decides the cadence (3–5s) and when to stop —
 * `transcriptionPoller.container.tsx` does both, app-wide, so a job keeps being watched after the
 * user navigates away from the page that started it.
 */
export const pollTranscriptionStatus = createAsyncThunk<
  Transcription,
  string,
  {rejectValue: ApiError}
>(
  'transcriptions/pollStatus',
  async (transcriptionId, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const url = `${getWorkerApiUrl()}/rest/v1/transcriptions/${transcriptionId}/status`;

      const response = await apiFetch(url, {
        headers: {'Authorization': `Bearer ${jwt}`},
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        return rejectWithValue(toApiError(response.status, errorText, 'Could not read the transcription status'));
      }

      const data: TranscriptionStatusResponse = await response.json();
      const transcription = toTranscription(data.transcription as WorkerTranscription);

      if (transcription.status !== 'processing') {
        // The row's r2_key and size only became real now, so any cached list is wrong.
        transcriptionsEtags.clear();
      }

      return transcription;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(toNetworkError(error, 'Could not read the transcription status'));
    }
  }
);

export const downloadTranscriptionCsv = createAsyncThunk(
  'transcriptions/downloadCsv',
  async ({csvR2Key}: { csvR2Key: string }, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/transcriptions/${csvR2Key}/download`;

      debugLog('Downloading CSV from:', url);
      const response = await apiFetch(url, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed with status', response.status, ':', errorText);
        throw new Error(`Failed to download CSV: ${response.status} - ${errorText}`);
      }

      // Get the CSV content as text
      const csvContent = await response.text();

      debugLog('CSV downloaded successfully, size:', csvContent.length);
      return csvContent;
    } catch (error) {
      // Check for 401/auth errors and auto-logout
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);
